/**
 * Sign-in flow — public API.
 *
 * Import from this file, not from the sub-modules directly.
 */

export type {
  SignInUserData,
  SignInState,
  SignInCommand,
  SignInResult,
  SignInSync,
  SignInAction,
  SignInDeps,
  SignInStoreDeps,
} from './types';

export { signInReducer } from './reducer';
export { signInEffects } from './effects';
export { FlowStore } from '../../shared/flow-store';

import { FlowStore } from '../../shared/flow-store';
import { signInReducer, INITIAL_STATE } from './reducer';
import { signInEffects } from './effects';
import type { SignInState, SignInAction, SignInDeps, SignInStoreDeps } from './types';

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Returns true when the flow is in an async (loading) step. */
export function isSignInLoading(state: SignInState): boolean {
  switch (state.step) {
    case 'LOOKING_UP_EMAIL':
    case 'SIGNING_IN':
    case 'SENDING_RESET_CODE':
    case 'VERIFYING_SIGN_UP':
    case 'VERIFYING_RESET':
      return true;
    default:
      return false;
  }
}

/** Maps a failed command to the appropriate typed failure action. */
export function signInDeriveFailure(action: SignInAction, err: unknown): SignInAction {
  const msg = err instanceof Error ? err.message : 'Something went wrong.';
  switch (action.type) {
    case 'SUBMIT_EMAIL':          return { type: 'EMAIL_LOOKUP_FAIL', error: msg };
    case 'SUBMIT_PASSWORD':       return { type: 'SIGN_IN_FAIL', error: msg };
    case 'SUBMIT_RESET_PASSWORD': return { type: 'RESET_CODE_FAIL', error: msg };
    case 'SUBMIT_VERIFICATION':   return { type: 'VERIFICATION_FAIL', error: msg };
    case 'RESEND_CODE':           return { type: 'RESEND_CODE_FAIL', error: msg };
    default:                      return { type: 'RESET' };
  }
}

// ─── Store factory ────────────────────────────────────────────────────────────

/**
 * Creates a fully-wired sign-in store.
 * The session provides the AuthService, AuthObservabilityService, and AuthLogger.
 */
export function createSignInStore(deps: SignInStoreDeps): FlowStore<SignInState, SignInAction, SignInDeps> {
  return new FlowStore<SignInState, SignInAction, SignInDeps>({
    name: 'SIGN_IN',
    initialState: INITIAL_STATE,
    reducer: signInReducer,
    effects: signInEffects,
    deriveFailure: signInDeriveFailure,
    deps: { service: deps.service, observability: deps.observability, eventBus: deps.eventBus, session: deps.session },
    observability: deps.observability,
  });
}
